import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useAuth, API } from '../App';
import { downloadArchive } from '../lib/downloadArchive';
import { useT } from '../i18n';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import ConfirmDialog from './ConfirmDialog';
import HelpHint from './HelpHint';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import {
  Plus,
  Edit,
  Trash2,
  ExternalLink,
  DollarSign,
  Search,
  Copy,
  TrendingUp,
  Gift,
  Upload,
  FileArchive,
  Globe,
  Code,
  Image,
  Download,
  FileCode,
  FileText,
  FolderOpen,
  Save,
  Archive,
  ShieldCheck,
  Eye,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import BundleAudit from './BundleAudit';
import BundlePreview, { bundleUrl, openBundle } from './BundlePreview';
import XpnsrPageHeader from './XpnsrPageHeader';
import XpnsrEmpty from './XpnsrEmpty';
import { XpnsrCardSkeleton, XpnsrTableSkeleton } from './XpnsrSkeleton';

export default function Offers() {
  const { user } = useAuth();
  const { t } = useT();
  const [offers, setOffers] = useState([]);
  const [affiliateNetworks, setAffiliateNetworks] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const [searchParams, setSearchParams] = useSearchParams();
  const [createDialogOpen, setCreateDialogOpen] = useState(searchParams.get('new') === '1');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);

  // Strip ?new=1 after dialog opens
  useEffect(() => {
    if (searchParams.get('new') === '1') {
      const next = new URLSearchParams(searchParams);
      next.delete('new');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line
  }, []);
  const [uploadData, setUploadData] = useState({
    name: '',
    description: '',
    payout: 0,
    currency: 'USD',
    category: '',
    network: '',
    countries: '',
  });
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Entry-point picker state
  const [entryPickerOpen, setEntryPickerOpen] = useState(false);
  const [detectedPages, setDetectedPages] = useState([]);
  const [pendingUploadResult, setPendingUploadResult] = useState(null);
  const [selectedEntry, setSelectedEntry] = useState('');

  // File editor state
  // Funnel check dialog ({id, name} or null).
  const [auditItem, setAuditItem] = useState(null);
  // Preview of an uploaded offer page: the row or null (closed).
  const [previewItem, setPreviewItem] = useState(null);
  const [fileEditorOpen, setFileEditorOpen] = useState(false);
  const [fileEditorOffer, setFileEditorOffer] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [activeFile, setActiveFile] = useState(null);
  const [fileContent, setFileContent] = useState('');
  const [fileSaving, setFileSaving] = useState(false);
  const [fileLoading, setFileLoading] = useState(false);
  const [fileDirty, setFileDirty] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    url: '',
    description: '',
    payout: 0,
    currency: 'USD',
    category: '',
    network: '',
    affiliate_network_id: '',
    countries: [],
    daily_cap: null,
    cap_timezone: 'UTC',
    fallback_offer_id: null,
    integration_id: '',
    integration_params: {},
    integration_routes: [],
    notify_telegram_bot: '',
    notify_telegram_chat: '',
    notify_telegram_topic: '',
  });
  const [countryInput, setCountryInput] = useState('');
  const [valueRows, setValueRows] = useState([]);

  useEffect(() => {
    axios.get(API + '/integrations').then((r) => setIntegrations(r.data || [])).catch(() => {});
  }, []);

  // Lead-integration catalogue: which per-offer fields (gi, funnel name, …)
  // each partner type needs. null = catalogue unavailable (endpoint failed /
  // older backend) → the static single `gi` input keeps working as before.
  const [leadTypes, setLeadTypes] = useState(null);
  useEffect(() => {
    axios
      .get(API + '/lead-integration-types')
      .then((r) => {
        const types = r.data && Array.isArray(r.data.types) ? r.data.types : [];
        if (types.length) setLeadTypes(types);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    axios
      .get(API + '/affiliate-networks')
      .then((r) => setAffiliateNetworks(r.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchOffers();
  }, []);

  const fetchOffers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/offers`);
      setOffers(response.data);
    } catch (error) {
      console.error('Error fetching offers:', error);
      toast.error(t('Error loading offers'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.name || !formData.url) {
      toast.error(t('Please fill in name and URL'));
      return;
    }
    const missingParams = missingRouteParams();
    if (missingParams.length) {
      toast.error(t('Missing fields') + ': ' + missingParams.join(', '));
      return;
    }
    try {
      const response = await axios.post(`${API}/offers`, { ...formData, values: rowsToValues(valueRows) });
      setOffers([response.data, ...offers]);
      setCreateDialogOpen(false);
      resetForm();
      toast.success(t('Offer created'));
    } catch (error) {
      toast.error(t('Error creating offer'));
    }
  };

  const handleUpdate = async () => {
    if (!editingOffer) return;
    const missingParams = missingRouteParams();
    if (missingParams.length) {
      toast.error(t('Missing fields') + ': ' + missingParams.join(', '));
      return;
    }
    try {
      const response = await axios.put(`${API}/offers/${editingOffer.id}`, { ...formData, values: rowsToValues(valueRows) });
      setOffers(offers.map((o) => (o.id === editingOffer.id ? response.data : o)));
      setEditDialogOpen(false);
      setEditingOffer(null);
      resetForm();
      toast.success(t('Offer updated'));
    } catch (error) {
      toast.error(t('Error updating offer'));
    }
  };

  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleDelete = async (offerId) => {
    try {
      await axios.delete(`${API}/offers/${offerId}`);
      setOffers(offers.filter((o) => o.id !== offerId));
      setDeleteTarget(null);
      toast.success(t('Offer archived'));
    } catch (error) {
      toast.error(t('Error deleting offer'));
    }
  };

  const openEdit = (offer) => {
    setEditingOffer(offer);
    setFormData({
      name: offer.name,
      url: offer.url,
      description: offer.description || '',
      payout: offer.payout || 0,
      currency: offer.currency || 'USD',
      category: offer.category || '',
      network: offer.network || '',
      affiliate_network_id: offer.affiliate_network_id || '',
      countries: offer.countries || [],
      daily_cap: offer.daily_cap || null,
      cap_timezone: offer.cap_timezone || 'UTC',
      fallback_offer_id: offer.fallback_offer_id || null,
      integration_id: offer.integration_id || '',
      integration_params: offer.integration_params || {},
      integration_routes: offer.integration_routes || [],
      notify_telegram_bot: offer.notify_telegram_bot || '',
      notify_telegram_chat: offer.notify_telegram_chat || '',
      notify_telegram_topic: offer.notify_telegram_topic || '',
    });
    setValueRows(Object.entries(offer.values || {}).map(([key, value]) => ({ key, value })));
    setEditDialogOpen(true);
  };

  // Per-offer fields for a route, resolved via the selected integration's type
  // in the lead-integration catalogue. Returns null when the catalogue is
  // unavailable / no integration picked / the type declares no offer fields —
  // callers then fall back to the static `gi` input.
  const routeOfferFields = (route) => {
    const integ = integrations.find((it) => it.id === route.integration_id);
    const ct = integ && (leadTypes || []).find((ti) => ti.type === integ.type);
    const fields = ct && Array.isArray(ct.offer_fields) ? ct.offer_fields : null;
    return fields && fields.length ? fields : null;
  };

  // Required per-offer fields (catalogue) missing on active routes. Empty when
  // the catalogue is unavailable, so saves behave exactly as before.
  const missingRouteParams = () => {
    const missing = new Set();
    (formData.integration_routes || []).forEach((route) => {
      if (route.is_active === false || !route.integration_id) return;
      (routeOfferFields(route) || []).forEach((f) => {
        if (f.required && !String((route.params || {})[f.key] || '').trim()) missing.add(t(f.label || f.key));
      });
    });
    return [...missing];
  };

  const addRoute = () => setFormData((prev) => ({ ...prev, integration_routes: [...(prev.integration_routes || []), { integration_id: '', params: {}, weight: 100, is_active: true }] }));
  const updateRoute = (i, patch) => setFormData((prev) => { const routes = [...(prev.integration_routes || [])]; routes[i] = { ...routes[i], ...patch }; return { ...prev, integration_routes: routes }; });
  const removeRoute = (i) => setFormData((prev) => ({ ...prev, integration_routes: (prev.integration_routes || []).filter((_, idx) => idx !== i) }));
  const rowsToValues = (rows) => {
    const obj = {};
    (rows || []).forEach((r) => {
      const k = (r.key || '').trim();
      if (k) obj[k] = r.value != null ? String(r.value) : '';
    });
    return obj;
  };
  const addValueRow = () => setValueRows((prev) => [...(prev || []), { key: '', value: '' }]);
  const updateValueRow = (i, field, val) => setValueRows((prev) => { const rows = [...(prev || [])]; rows[i] = { ...rows[i], [field]: val }; return rows; });
  const removeValueRow = (i) => setValueRows((prev) => (prev || []).filter((_, idx) => idx !== i));

  const resetForm = () => {
    setFormData({ name: '', url: '', description: '', payout: 0, currency: 'USD', category: '', network: '', affiliate_network_id: '', countries: [], daily_cap: null, cap_timezone: 'UTC', fallback_offer_id: null, integration_id: '', integration_params: {}, integration_routes: [], notify_telegram_bot: '', notify_telegram_chat: '', notify_telegram_topic: '' });
    setCountryInput('');
    setValueRows([]);
  };

  const addCountry = () => {
    const code = countryInput.trim().toUpperCase();
    if (code && code.length === 2 && !formData.countries.includes(code)) {
      setFormData((prev) => ({ ...prev, countries: [...prev.countries, code] }));
      setCountryInput('');
    }
  };

  const removeCountry = (code) => {
    setFormData((prev) => ({ ...prev, countries: prev.countries.filter((c) => c !== code) }));
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url);
    toast.success(t('URL copied'));
  };

  const handleUpload = async (overrideIndexFile) => {
    if (!uploadData.name || !uploadFile) {
      toast.error(t('Please fill in name and select ZIP file'));
      return;
    }
    if (!uploadFile.name.endsWith('.zip')) {
      toast.error(t('Only ZIP files are supported'));
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', uploadFile);
    formData.append('name', uploadData.name);
    formData.append('description', uploadData.description);
    formData.append('payout', uploadData.payout);
    formData.append('currency', uploadData.currency);
    formData.append('category', uploadData.category);
    formData.append('network', uploadData.network);
    formData.append('countries', uploadData.countries);
    if (overrideIndexFile) formData.append('index_file', overrideIndexFile);

    try {
      const response = await axios.post(`${API}/offers/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const returnedPages = response.data.pages || [];

      // If multiple pages and no override yet, show picker
      if (returnedPages.length > 1 && !overrideIndexFile) {
        setDetectedPages(returnedPages);
        setSelectedEntry(response.data.index_file || returnedPages[0]);
        setPendingUploadResult(response.data);
        setUploadDialogOpen(false);
        setEntryPickerOpen(true);
        return;
      }

      setOffers([response.data, ...offers]);
      setUploadDialogOpen(false);
      setEntryPickerOpen(false);
      setUploadData({
        name: '',
        description: '',
        payout: 0,
        currency: 'USD',
        category: '',
        network: '',
        countries: '',
      });
      setUploadFile(null);
      toast.success(t('Offer uploaded successfully'));
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || t('Error uploading offer'));
    } finally {
      setUploading(false);
    }
  };

  const handleEntryPointConfirm = async () => {
    if (!pendingUploadResult) return;
    try {
      await axios.delete(`${API}/offers/${pendingUploadResult.id}`);
    } catch (_) { /* best effort */ }
    await handleUpload(selectedEntry);
    setPendingUploadResult(null);
    setDetectedPages([]);
  };

  const handleEntryPickerSkip = () => {
    if (pendingUploadResult) {
      setOffers([pendingUploadResult, ...offers]);
    }
    setEntryPickerOpen(false);
    setPendingUploadResult(null);
    setDetectedPages([]);
    setUploadData({ name: '', description: '', payout: 0, currency: 'USD', category: '', network: '', countries: '' });
    setUploadFile(null);
    toast.success(t('Offer uploaded successfully'));
  };

  // File editor functions
  const openFileEditor = async (offer) => {
    setFileEditorOffer(offer);
    setFileEditorOpen(true);
    setActiveFile(null);
    setFileContent('');
    setFileDirty(false);
    try {
      setFileLoading(true);
      const res = await axios.get(`${API}/offers/${offer.id}/files`);
      setFileList(res.data.files || []);
    } catch (err) {
      toast.error(t('Error loading files'));
      setFileEditorOpen(false);
    } finally {
      setFileLoading(false);
    }
  };

  const openFile = async (filePath) => {
    if (fileDirty && !window.confirm(t('Discard unsaved changes?'))) return;
    try {
      setFileLoading(true);
      const res = await axios.get(`${API}/offers/${fileEditorOffer.id}/files/${filePath}`);
      setActiveFile(filePath);
      setFileContent(res.data.content);
      setFileDirty(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Error loading file'));
    } finally {
      setFileLoading(false);
    }
  };

  const editorRefreshFiles = async () => {
    if (!fileEditorOffer) return;
    try {
      const res = await axios.get(API + "/offers/" + fileEditorOffer.id + "/files");
      setFileList(res.data.files || []);
    } catch (err) {
      toast.error(t('Error loading files'));
    }
  };

  const editorDeleteFile = async (filePath) => {
    if (!fileEditorOffer || !filePath) return;
    if (!window.confirm(t('Delete this file?') + ' ' + filePath)) return;
    try {
      await axios.delete(API + "/offers/" + fileEditorOffer.id + "/files/" + filePath);
      if (activeFile === filePath) { setActiveFile(null); setFileContent(''); setFileDirty(false); }
      toast.success(t('File deleted'));
      await editorRefreshFiles();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Error deleting file'));
    }
  };

  const editorUploadFile = async (fileObj) => {
    if (!fileObj || !fileEditorOffer) return;
    try {
      setFileLoading(true);
      const fd = new FormData();
      fd.append('file', fileObj);
      await axios.post(API + "/offers/" + fileEditorOffer.id + "/files", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t('File uploaded'));
      await editorRefreshFiles();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Error uploading file'));
    } finally {
      setFileLoading(false);
    }
  };

  const saveFile = async () => {
    if (!activeFile || !fileEditorOffer) return;
    try {
      setFileSaving(true);
      await axios.put(`${API}/offers/${fileEditorOffer.id}/files/${activeFile}`, { content: fileContent });
      setFileDirty(false);
      toast.success(t('File saved'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Error saving file'));
    } finally {
      setFileSaving(false);
    }
  };

  const resetUploadForm = () => {
    setUploadData({
      name: '',
      description: '',
      payout: 0,
      currency: 'USD',
      category: '',
      network: '',
      countries: '',
    });
    setUploadFile(null);
  };

  const filtered = offers.filter(
    (o) =>
      o.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      o.url.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (o.network || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const exportCSV = () => {
    const rows = [['Name', 'Network', 'Category', 'Payout', 'Clicks', 'Conversions', 'EPC', 'Geo', 'Status']];
    filtered.forEach((o) => {
      const epc = (o.total_clicks || 0) > 0 ? ((o.conversions || 0) * (Number(o.payout) || 0) / o.total_clicks).toFixed(4) : '0.0000';
      rows.push([
        o.name, o.network || '', o.category || '',
        `${(Number(o.payout) || 0).toFixed(2)} ${o.currency || 'USD'}`,
        o.total_clicks || 0, o.conversions || 0, epc,
        (o.countries || []).join(', '), o.is_active ? 'Active' : 'Inactive',
      ]);
    });
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `offers-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(t('CSV exported'));
  };

  const currencies = ['USD', 'EUR', 'RUB', 'GBP', 'UAH'];
  const offerCategories = [
    { value: 'nutra', label: t('Nutra') },
    { value: 'gambling', label: t('Gambling') },
    { value: 'dating', label: t('Dating') },
    { value: 'finance', label: t('Finance') },
    { value: 'ecommerce', label: 'E-commerce' },
    { value: 'crypto', label: t('Crypto') },
    { value: 'sweepstakes', label: t('Sweepstakes') },
    { value: 'other', label: t('Other') },
  ];

  const OfferFormFields = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>{t('Name')}</Label>
          <Input
            data-testid="offer-name-input"
            value={formData.name}
            onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
            placeholder={t('My offer')}
          />
        </div>
        <div className="space-y-2">
          <Label>{t('Affiliate network')} <HelpHint text={t("A reusable partner network from the Affiliate networks section: postback template, offer params and per-network reports. Optional.")} /></Label>
          <Select
            value={formData.affiliate_network_id || 'none'}
            onValueChange={(value) =>
              setFormData((prev) => ({
                ...prev,
                affiliate_network_id: value === 'none' ? '' : value,
              }))
            }
          >
            <SelectTrigger data-testid="offer-affiliate-network-select">
              <SelectValue placeholder={t('None')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">{t('None')}</SelectItem>
              {affiliateNetworks.map((n) => (
                <SelectItem key={n.id} value={n.id}>
                  {n.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-2">
        <Label>{t('Offer URL')} <HelpHint text={t("Where the click goes for a plain redirect offer. Macros work: {click_id}, {country_code}, {gclid} and more. For form offers (lead to CRM) the URL can be anything.")} /></Label>
        <Input
          data-testid="offer-url-input"
          value={formData.url}
          onChange={(e) => setFormData((prev) => ({ ...prev, url: e.target.value }))}
          placeholder="https://offer.example.com/click?sub={clickid}"
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label>{t('Payout')}</Label>
          <Input
            data-testid="offer-payout-input"
            type="number"
            step="0.01"
            value={formData.payout}
            onChange={(e) => setFormData((prev) => ({ ...prev, payout: parseFloat(e.target.value) || 0 }))}
          />
        </div>
        <div className="space-y-2">
          <Label>{t('Currency')}</Label>
          <Select
            value={formData.currency}
            onValueChange={(value) => setFormData((prev) => ({ ...prev, currency: value }))}
          >
            <SelectTrigger data-testid="offer-currency-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {currencies.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>{t('Category')}</Label>
          <Select
            value={formData.category}
            onValueChange={(value) => setFormData((prev) => ({ ...prev, category: value }))}
          >
            <SelectTrigger data-testid="offer-category-select">
              <SelectValue placeholder={t('Category')} />
            </SelectTrigger>
            <SelectContent>
              {offerCategories.map((c) => (
                <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <details className="rounded-md border border-border p-3" data-testid="offer-advanced">
        <summary className="cursor-pointer text-sm font-medium">{t('Additional settings')}</summary>
        <p className="mt-1 text-xs text-muted-foreground">{t('Description, countries, daily cap and fallback offer.')}</p>
        <div className="mt-3 space-y-4">
          <div className="space-y-2">
            <Label>{t('Description')}</Label>
            <Textarea
              data-testid="offer-description-input"
              value={formData.description}
              onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
              placeholder={t('Offer description')}
            />
          </div>
          <div className="space-y-2">
            <Label>{t('Countries (ISO codes, e.g. US, RU)')}</Label>
            <div className="flex gap-2">
              <Input
                value={countryInput}
                onChange={(e) => setCountryInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCountry())}
                placeholder="RU"
                className="w-24"
              />
              <Button type="button" variant="outline" onClick={addCountry}>+</Button>
            </div>
            {formData.countries.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {formData.countries.map((code) => (
                  <Badge key={code} variant="secondary" className="cursor-pointer" onClick={() => removeCountry(code)}>
                    {code} x
                  </Badge>
                ))}
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>{t('Daily cap')} <HelpHint text={t("Max conversions per offer per day. When the cap is hit, traffic goes to the fallback offer. Empty = no limit.")} /></Label>
              <Input
                type="number"
                min="0"
                value={formData.daily_cap || ''}
                onChange={(e) => setFormData((prev) => ({ ...prev, daily_cap: e.target.value ? parseInt(e.target.value, 10) : null }))}
                placeholder={t('Unlimited')}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('Cap timezone')} <HelpHint text={t("Time zone in which the daily cap is counted and the counter resets.")} /></Label>
              <Select
                value={formData.cap_timezone}
                onValueChange={(value) => setFormData((prev) => ({ ...prev, cap_timezone: value }))}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['UTC','US/Eastern','US/Central','US/Pacific','Europe/London','Europe/Berlin','Europe/Moscow','Asia/Tokyo','Asia/Shanghai','Asia/Kolkata','Australia/Sydney','Pacific/Auckland','America/Sao_Paulo','Africa/Cairo','Asia/Dubai','Asia/Bangkok'].map(tz => (
                    <SelectItem key={tz} value={tz}>{tz}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('Fallback offer')} <HelpHint text={t("Fallback offer: traffic goes here once the main offer hits its daily cap.")} /></Label>
              <Select
                value={formData.fallback_offer_id || '_none'}
                onValueChange={(value) => setFormData((prev) => ({ ...prev, fallback_offer_id: value === '_none' ? null : value }))}
              >
                <SelectTrigger><SelectValue placeholder={t('None')} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">{t('None')}</SelectItem>
                  {offers.filter(o => o.id !== editingOffer?.id).map(o => (
                    <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </details>
      <details
        className="rounded-md border border-border p-3"
        data-testid="offer-routing"
        open={(formData.integration_routes || []).length > 0}
      >
        <summary className="cursor-pointer text-sm font-medium">{t('Affiliate routing (form offers)')}</summary>
        <div className="space-y-2 mt-3">
          <p className="text-xs text-muted-foreground">{t('Route this offer to one or more partners with weights (split/rotation). Inactive partners are skipped (e.g. until terms are agreed). The lead goes by weight; on rejection it falls back to the next partner.')}</p>
          <div className="space-y-2">
            {(formData.integration_routes || []).map((route, i) => {
              // Catalogue-driven per-offer fields for the selected partner type;
              // null → static single `gi` input (pre-catalogue behavior).
              const offerFields = routeOfferFields(route);
              return (
              <div key={i} className="space-y-2 rounded-md border border-border p-3">
                {/* Row 1: the partner, on its own row so it never collapses when
                    the partner type adds per-offer fields (was one grid row). */}
                <div className="grid grid-cols-[minmax(0,1fr)_auto_auto] items-end gap-2">
                  <div className="min-w-0 space-y-1">
                    <Label className="text-xs">{t('Integration')} <HelpHint text={t("The partner/CRM the lead is sent to. Set up once in the Integrations section.")} /></Label>
                    <Select value={route.integration_id || '_none'} onValueChange={(value) => updateRoute(i, { integration_id: value === '_none' ? '' : value })}>
                      <SelectTrigger className="w-full"><SelectValue placeholder={t('None')} /></SelectTrigger>
                      <SelectContent className="min-w-[16rem]">
                        <SelectItem value="_none">{t('None')}</SelectItem>
                        {integrations.map((it) => (<SelectItem key={it.id} value={it.id}>{it.name} ({it.type})</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <label className="flex items-center gap-1 text-xs pb-2 cursor-pointer whitespace-nowrap">
                    <input type="checkbox" checked={route.is_active !== false} onChange={(e) => updateRoute(i, { is_active: e.target.checked })} />
                    {t('Active')} <HelpHint text={t("Off = the partner is temporarily skipped (e.g. until launch is agreed); its settings are kept.")} />
                  </label>
                  <Button variant="outline" size="icon" className="mb-0.5" aria-label={t('Remove partner route')} title={t('Remove partner route')} onClick={() => removeRoute(i)}><Trash2 className="w-4 h-4" /></Button>
                </div>
                {/* Row 2: per-offer fields of the partner type (or gi) and the weight. */}
                <div className="grid grid-cols-1 items-end gap-2 sm:grid-cols-2 lg:grid-cols-4">
                  {offerFields ? offerFields.map((f) => (
                    <div key={f.key} className="min-w-0 space-y-1">
                      <Label className="text-xs">{t(f.label || f.key)}{f.required ? <span className="text-destructive"> *</span> : null}{f.hint ? <> <HelpHint text={t(f.hint)} /></> : null}</Label>
                      <Input
                        type={f.secret ? 'password' : 'text'}
                        placeholder={f.default != null ? String(f.default) : ''}
                        value={(route.params && route.params[f.key]) || ''}
                        onChange={(e) => updateRoute(i, { params: { ...(route.params || {}), [f.key]: e.target.value } })}
                      />
                    </div>
                  )) : (
                    <div className="min-w-0 space-y-1">
                      <Label className="text-xs">gi <HelpHint text={t("The offer's Box/Goal ID at the partner — which product/box receives the lead. The partner provides the value (e.g. 420).")} /></Label>
                      <Input placeholder="420" value={(route.params && route.params.gi) || ''} onChange={(e) => updateRoute(i, { params: { ...(route.params || {}), gi: e.target.value } })} />
                    </div>
                  )}
                  <div className="min-w-0 space-y-1">
                    <Label className="text-xs">{t('Weight')} <HelpHint text={t("Share of leads to this partner when there are several (split/rotation). E.g. 70 and 30 = 70% and 30%.")} /></Label>
                    <Input type="number" value={route.weight ?? 100} onChange={(e) => updateRoute(i, { weight: parseInt(e.target.value, 10) || 0 })} />
                  </div>
                </div>
              </div>
              );
            })}
            {(formData.integration_routes || []).length === 0 && (
              <p className="text-xs text-muted-foreground">{t('No partner routes — this is a plain redirect/upload offer.')}</p>
            )}
          </div>
          <Button variant="outline" size="sm" onClick={addRoute}>
            <Plus className="w-4 h-4 mr-1" /> {t('Add partner route')}
          </Button>
          {editingOffer && editingOffer.id && (formData.integration_routes || []).length > 0 && (
            <div className="rounded-md border border-border bg-surface-2 p-2 mt-2 flex items-center gap-2">
              <span className="text-xs text-muted-foreground shrink-0">{t('Lead-form URL')}: <HelpHint text={t("A per-offer form endpoint for THIS offer only. Usually NOT needed — use the shared /api/lead (see docs). Only for binding a form to one specific offer.")} /></span>
              <code className="text-xs font-mono truncate flex-1">{API + '/offers/' + editingOffer.id + '/lead'}</code>
              <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0 shrink-0" aria-label={t('Copy lead-form URL')} title={t('Copy lead-form URL')} onClick={() => { navigator.clipboard.writeText(API + '/offers/' + editingOffer.id + '/lead'); toast.success(t('Copied')); }}>
                <Copy className="w-3.5 h-3.5" />
              </Button>
            </div>
          )}
          <div className="space-y-1 mt-2">
            <Label className="text-xs">{t('Telegram lead alerts')} <span className="text-muted-foreground font-normal">{t('(sent on EVERY submit — even test/error)')}</span> <HelpHint text={t("Telegram notification on EVERY form submit (accepted/rejected/test). Separate from the campaign Telegram postback (that fires on a confirmed conversion). Bot token from @BotFather; group Chat ID starts with -100…")} /></Label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <Input placeholder={t('Bot token')} value={formData.notify_telegram_bot || ''} onChange={(e) => setFormData((prev) => ({ ...prev, notify_telegram_bot: e.target.value }))} />
              <Input placeholder={t('Chat ID')} value={formData.notify_telegram_chat || ''} onChange={(e) => setFormData((prev) => ({ ...prev, notify_telegram_chat: e.target.value }))} />
              <Input placeholder={t('Topic ID (optional)')} value={formData.notify_telegram_topic || ''} onChange={(e) => setFormData((prev) => ({ ...prev, notify_telegram_topic: e.target.value }))} />
            </div>
          </div>
          <div className="space-y-2 mt-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs">{t('Offer values')} <HelpHint text={t('Named key/value pairs injected into landing/offer/postback URLs and lead form templates via the {offer_value:NAME} macro.')} /></Label>
              <Button variant="outline" size="sm" onClick={addValueRow}>
                <Plus className="w-4 h-4 mr-1" /> {t('Add value')}
              </Button>
            </div>
            {(valueRows || []).length === 0 ? (
              <p className="text-xs text-muted-foreground">{t('No offer values. Add a key like price, then use {offer_value:price} in URLs.')}</p>
            ) : (
              <div className="space-y-2">
                {valueRows.map((row, i) => (
                  <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2">
                    <Input placeholder={t('Key (e.g. price)')} value={row.key} onChange={(e) => updateValueRow(i, 'key', e.target.value)} />
                    <Input placeholder={t('Value')} value={row.value} onChange={(e) => updateValueRow(i, 'value', e.target.value)} />
                    <Button variant="outline" size="icon" aria-label={t('Remove value')} title={t('Remove value')} onClick={() => removeValueRow(i)}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </details>
    </div>
  );

  if (loading) {
    return (
      <div className="p-8 space-y-6" data-testid="offers-skeleton">
        <XpnsrPageHeader kicker={t('Catalog')} title={t('Offers')} subtitle={t('Manage your offers and creatives')} />
        <XpnsrTableSkeleton rows={6} columns={4} />
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6" data-testid="offers-view">
      <BundleAudit kind="offers" item={auditItem} onClose={() => setAuditItem(null)} />
      <BundlePreview item={previewItem} onClose={() => setPreviewItem(null)} openLabel={t('Open offer')} />
      <XpnsrPageHeader
        kicker={t('Catalog')}
        title={
          <>
            {t('Offers')} <em>({offers.length})</em>
          </>
        }
        subtitle={t('Configure offers, payouts and target geos. Upload ZIPs or link external networks.')}
        actions={
          <>
          <Button asChild variant="outline" data-testid="offers-view-archived">
            <Link to="/archive">
              <Archive className="w-4 h-4 mr-2" />
              {t('View archived')}
            </Link>
          </Button>
          <Button variant="outline" onClick={exportCSV} title={t('Export offers to CSV')}>
            <Download className="w-4 h-4 mr-2" />
            {t('Export CSV')}
          </Button>
          <Dialog open={uploadDialogOpen} onOpenChange={(open) => { setUploadDialogOpen(open); if (!open) resetUploadForm(); }}>
            <DialogTrigger asChild>
              <Button variant="outline" onClick={resetUploadForm}>
                <Upload className="w-4 h-4 mr-2" />
                {t('Upload ZIP')}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto overflow-x-hidden">
              <DialogHeader>
                <DialogTitle>{t('Upload Offer ZIP')}</DialogTitle>
                <DialogDescription>{t('Upload a ZIP file containing offer files')}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('Name')}</Label>
                    <Input
                      value={uploadData.name}
                      onChange={(e) => setUploadData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder={t('My offer')}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('Affiliate network')} <HelpHint text={t("A reusable partner network from the Affiliate networks section: postback template, offer params and per-network reports. Optional.")} /></Label>
                    <Input
                      value={uploadData.network}
                      onChange={(e) => setUploadData(prev => ({ ...prev, network: e.target.value }))}
                      placeholder={t('CPA Network')}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>{t('Payout')}</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={uploadData.payout}
                      onChange={(e) => setUploadData(prev => ({ ...prev, payout: parseFloat(e.target.value) || 0 }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('Currency')}</Label>
                    <Select
                      value={uploadData.currency}
                      onValueChange={(value) => setUploadData(prev => ({ ...prev, currency: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {currencies.map((c) => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{t('Category')}</Label>
                    <Select
                      value={uploadData.category}
                      onValueChange={(value) => setUploadData(prev => ({ ...prev, category: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t('Category')} />
                      </SelectTrigger>
                      <SelectContent>
                        {offerCategories.map((c) => (
                          <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>{t('Description')}</Label>
                  <Textarea
                    value={uploadData.description}
                    onChange={(e) => setUploadData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder={t('Offer description')}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('Countries (comma-separated ISO codes, e.g. US,RU,DE)')}</Label>
                  <Input
                    value={uploadData.countries}
                    onChange={(e) => setUploadData(prev => ({ ...prev, countries: e.target.value }))}
                    placeholder={t('US,RU,DE')}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('ZIP File (max 100MB)')}</Label>
                  <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-6 text-center">
                    {uploadFile ? (
                      <div className="flex items-center justify-center gap-2">
                        <FileArchive className="w-6 h-6 text-primary" />
                        <span className="font-medium">{uploadFile.name}</span>
                        <Button variant="ghost" size="sm" onClick={() => setUploadFile(null)}>
                          {t('Change')}
                        </Button>
                      </div>
                    ) : (
                      <div>
                        <Upload className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                        <p className="text-sm text-muted-foreground mb-2">{t('Drag & drop or click to select')}</p>
                        <Button variant="outline" onClick={() => document.getElementById('zip-upload').click()}>
                          {t('Select ZIP file')}
                        </Button>
                        <input
                          id="zip-upload"
                          type="file"
                          accept=".zip"
                          className="hidden"
                          onChange={(e) => setUploadFile(e.target.files[0])}
                        />
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {t('Upload a ZIP file containing HTML pages, images, and other assets. Supported: _preview.png for preview, PHP scripts for processing.')}
                  </p>
                </div>
              </div>
              <div className="flex justify-end space-x-2 pt-4">
                <Button variant="outline" onClick={() => setUploadDialogOpen(false)} disabled={uploading}>
                  {t('Cancel')}
                </Button>
                <Button onClick={() => handleUpload()} disabled={uploading}>
                  {uploading ? t('Uploading...') : t('Upload Offer')}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          <Dialog open={createDialogOpen} onOpenChange={(open) => { setCreateDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="create-offer-btn" onClick={resetForm}>
                <Plus className="w-4 h-4 mr-2" />
                {t('Add offer')}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto overflow-x-hidden">
              <DialogHeader>
                <DialogTitle>{t('New offer')}</DialogTitle>
                <DialogDescription>{t('Add an offer to use in campaigns')}</DialogDescription>
              </DialogHeader>
              {OfferFormFields()}
              <div className="flex justify-end space-x-2 pt-4">
                <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>{t('Cancel')}</Button>
                <Button data-testid="save-offer-btn" onClick={handleCreate}>{t('Create')}</Button>
              </div>
            </DialogContent>
          </Dialog>
          </>
        }
      />

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          data-testid="offer-search-input"
          className="pl-10"
          placeholder={t('Search offers…')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

       {filtered.length === 0 ? (
         <Card>
           <CardContent style={{ padding: 0 }}>
             <XpnsrEmpty
               icon={Gift}
               title={searchQuery ? t('Nothing found') : t('No offers')}
               subtitle={t('Create offers to monetize your traffic')}
               cta={!searchQuery ? (
                 <button
                   type="button"
                   className="xp-btn xp-btn-primary"
                   onClick={() => setCreateDialogOpen(true)}
                   data-testid="empty-create-offer"
                 >
                   <Plus style={{ width: 12, height: 12 }} /> {t('Create offer')}
                 </button>
               ) : null}
             />
           </CardContent>
         </Card>
       ) : (
         <Card>
           <div className="overflow-x-auto">
             <Table>
               <TableHeader>
                 <TableRow>
                   <TableHead className="whitespace-nowrap">{t('Name')}</TableHead>
                   <TableHead className="whitespace-nowrap">{t('Network')}</TableHead>
                   <TableHead className="whitespace-nowrap">{t('Category')}</TableHead>
                   <TableHead className="text-right whitespace-nowrap">{t('Payout')}</TableHead>
                   <TableHead className="text-right whitespace-nowrap">{t('Clicks')}</TableHead>
                   <TableHead className="text-right whitespace-nowrap">{t('Conversions')}</TableHead>
                   <TableHead className="text-right whitespace-nowrap">EPC</TableHead>
                   <TableHead className="whitespace-nowrap">{t('Geo')}</TableHead>
                   <TableHead className="whitespace-nowrap">{t('Cap')}</TableHead>
                   <TableHead className="whitespace-nowrap">{t('Status')}</TableHead>
                   <TableHead className="text-right whitespace-nowrap">{t('Actions')}</TableHead>
                 </TableRow>
               </TableHeader>
               <TableBody>
                 {filtered.map((offer) => (
                   <TableRow key={offer.id} data-testid={`offer-row-${offer.id}`}>
                     <TableCell className="whitespace-nowrap">
                       <div className="font-medium">{offer.name}</div>
                     </TableCell>
                     <TableCell className="whitespace-nowrap">
                       <span className="text-sm text-muted-foreground">{offer.network || '-'}</span>
                     </TableCell>
                     <TableCell className="whitespace-nowrap">
                       <Badge variant="outline">
                         {offerCategories.find((c) => c.value === offer.category)?.label || offer.category || '-'}
                       </Badge>
                     </TableCell>
                     <TableCell className="text-right font-medium text-primary whitespace-nowrap">
                       {offer.payout?.toFixed(2)} {offer.currency}
                     </TableCell>
                     <TableCell className="text-right whitespace-nowrap">{offer.total_clicks || 0}</TableCell>
                     <TableCell className="text-right whitespace-nowrap">{offer.conversions || 0}</TableCell>
                     <TableCell className="text-right font-medium whitespace-nowrap">
                       {offer.epc ? `$${(Number(offer.epc) || 0).toFixed(2)}` : '$0.00'}
                     </TableCell>
                     <TableCell className="whitespace-nowrap">
                       <div className="flex flex-wrap gap-1">
                         {(offer.countries || []).slice(0, 3).map((c) => (
                           <Badge key={c} variant="outline" className="text-xs">{c}</Badge>
                         ))}
                         {(offer.countries || []).length > 3 && (
                           <Badge variant="outline" className="text-xs">+{offer.countries.length - 3}</Badge>
                         )}
                       </div>
                     </TableCell>
                     <TableCell className="whitespace-nowrap">
                       {offer.daily_cap ? (
                         <div className="flex items-center gap-2">
                           <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                             <div
                               className="h-full rounded-full transition-all"
                               style={{
                                 width: `${Math.min(100, ((offer.conversions || 0) / offer.daily_cap) * 100)}%`,
                                 background: ((offer.conversions || 0) / offer.daily_cap) >= 0.9 ? 'rgb(var(--destructive))' : 'rgb(var(--tint))',
                               }}
                             />
                           </div>
                           <span className="text-xs tabular-nums text-muted-foreground">
                             {offer.conversions || 0}/{offer.daily_cap}
                           </span>
                         </div>
                       ) : (
                         <span className="text-xs text-muted-foreground">—</span>
                       )}
                     </TableCell>
                     <TableCell className="whitespace-nowrap">
                       <Badge variant={offer.is_active ? 'default' : 'secondary'}>
                         {offer.is_active ? t('Active') : t('Paused')}
                       </Badge>
                     </TableCell>
                     <TableCell className="text-right whitespace-nowrap">
                       <div className="flex justify-end gap-1">
                         <Button variant="ghost" size="sm" aria-label={t('Copy offer URL')} title={t('Copy offer URL')} onClick={() => copyUrl(bundleUrl(offer.url))}>
                           <Copy className="w-4 h-4" />
                         </Button>
                         {offer.is_uploaded && offer.url && (
                           <Button variant="ghost" size="sm" onClick={() => setPreviewItem(offer)} aria-label={t('Preview')} title={t('Preview')} data-testid={`offer-preview-${offer.id}`}>
                             <Eye className="w-4 h-4" />
                           </Button>
                         )}
                         {offer.is_uploaded && (
                           <Button variant="ghost" size="sm" onClick={() => openFileEditor(offer)} aria-label={t('Edit files')} title={t('Edit files')}>
                             <FileCode className="w-4 h-4" />
                           </Button>
                         )}
                         {offer.is_uploaded && (
                           <Button variant="ghost" size="sm" onClick={() => setAuditItem({ id: offer.id, name: offer.name })} aria-label={t('Check funnel')} title={t('Check funnel')}>
                             <ShieldCheck className="w-4 h-4" />
                           </Button>
                         )}
                         {offer.is_uploaded && offer.url && (
                           <Button variant="ghost" size="sm" onClick={() => openBundle(offer.url)} aria-label={t('Open offer')} title={t('Open offer')} data-testid={`offer-open-${offer.id}`}>
                             <ExternalLink className="w-4 h-4" />
                           </Button>
                         )}
                         <Button variant="ghost" size="sm" aria-label={t('Edit offer')} title={t('Edit offer')} onClick={() => openEdit(offer)} data-testid={`offer-edit-${offer.id}`}>
                           <Edit className="w-4 h-4" />
                         </Button>
                         {(offer.is_uploaded || offer.upload_path) && (
                           <Button
                             variant="ghost"
                             size="sm"
                             aria-label={t('Download ZIP')}
                             title={t('Download ZIP')}
                             onClick={() => downloadArchive(API, `/offers/${offer.id}/download`, offer.name).catch(() => toast.error(t('Download failed')))}
                             data-testid={`download-${offer.id}`}
                           >
                             <Download className="w-4 h-4" />
                           </Button>
                         )}
                         <Button variant="ghost" size="sm" aria-label={t('Delete offer')} title={t('Delete offer')} onClick={() => setDeleteTarget(offer)} data-testid={`offer-delete-${offer.id}`}>
                           <Trash2 className="w-4 h-4 text-destructive" />
                         </Button>
                       </div>
                     </TableCell>
                   </TableRow>
                 ))}
               </TableBody>
             </Table>
           </div>
         </Card>
       )}

      <Dialog open={editDialogOpen} onOpenChange={(open) => { setEditDialogOpen(open); if (!open) { setEditingOffer(null); resetForm(); } }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto overflow-x-hidden">
          <DialogHeader>
            <DialogTitle>{t('Edit offer')}</DialogTitle>
            <DialogDescription>{t('Change offer parameters')}</DialogDescription>
          </DialogHeader>
          {OfferFormFields()}
          <div className="flex justify-end space-x-2 pt-4">
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>{t('Cancel')}</Button>
            <Button data-testid="update-offer-btn" onClick={handleUpdate}>{t('Save')}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
        title={t('Archive offer')}
        description={t('This will move "{name}" to the archive. You can restore it or delete it permanently from the Archive page.').replace('{name}', deleteTarget?.name || '')}
        confirmLabel={t('Archive')}
        variant="default"
        onConfirm={() => handleDelete(deleteTarget?.id)}
      />

      {/* Entry-point picker dialog */}
      <Dialog open={entryPickerOpen} onOpenChange={(open) => { if (!open) handleEntryPickerSkip(); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('Choose entry point')}</DialogTitle>
            <DialogDescription>{t('Multiple HTML pages detected. Select the main page visitors will land on.')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[40vh] overflow-y-auto">
            {detectedPages.map((p) => (
              <label key={p} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${selectedEntry === p ? 'border-primary bg-primary/5' : 'hover:bg-muted'}`}>
                <input type="radio" name="offer-entry" value={p} checked={selectedEntry === p} onChange={() => setSelectedEntry(p)} className="accent-primary" />
                <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <span className="text-sm truncate">{p}</span>
              </label>
            ))}
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={handleEntryPickerSkip}>{t('Skip (use auto-detected)')}</Button>
            <Button onClick={handleEntryPointConfirm} disabled={uploading}>
              {uploading ? t('Uploading...') : t('Confirm')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* File editor dialog */}
      <Dialog open={fileEditorOpen} onOpenChange={(open) => { if (!open) { if (fileDirty && !window.confirm(t('Discard unsaved changes?'))) return; setFileEditorOpen(false); setFileEditorOffer(null); setActiveFile(null); setFileContent(''); setFileDirty(false); } }}>
        <DialogContent className="max-w-6xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderOpen className="w-5 h-5" />
              {fileEditorOffer?.name} — {t('File editor')}
            </DialogTitle>
            <DialogDescription>{t('Edit HTML, CSS, JS and other text files inside the uploaded archive')}</DialogDescription>
          </DialogHeader>
          <div className="flex gap-4" style={{ height: '65vh' }}>
            <div className="w-64 flex-shrink-0 border rounded-lg overflow-y-auto bg-muted/30">
              <div className="p-2 text-xs font-medium text-muted-foreground uppercase tracking-wide border-b flex items-center justify-between">
                <span>{t('Files')}</span>
                <label className="cursor-pointer text-primary hover:underline font-normal normal-case tracking-normal">
                  {t('+ Upload')}
                  <input type="file" className="hidden" onChange={(e) => { const fobj = e.target.files && e.target.files[0]; e.target.value = ""; editorUploadFile(fobj); }} />
                </label>
              </div>
              {fileLoading && !activeFile ? (
                <div className="p-4 text-sm text-muted-foreground">{t('Loading...')}</div>
              ) : (
                <div className="p-1">
                  {fileList.map((f) => (
                    <button
                      key={f.path}
                      onClick={() => f.editable && openFile(f.path)}
                      className={`w-full text-left px-3 py-1.5 rounded text-sm flex items-center gap-2 transition-colors ${
                        activeFile === f.path ? 'bg-primary/10 text-primary font-medium' : f.editable ? 'hover:bg-muted cursor-pointer' : 'text-muted-foreground cursor-default opacity-60'
                      }`}
                      title={f.editable ? f.path : t('Binary file — not editable')}
                    >
                      <FileText className="w-3.5 h-3.5 flex-shrink-0" />
                      <span className="truncate">{f.path}</span>
                      {f.path === fileEditorOffer?.index_file && (
                        <Badge variant="secondary" className="text-[10px] ml-auto flex-shrink-0">entry</Badge>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="flex-1 flex flex-col min-w-0">
              {activeFile ? (
                <>
                  <div className="flex items-center justify-between pb-2 border-b mb-2">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground min-w-0">
                      <FileCode className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate">{activeFile}</span>
                      {fileDirty && <Badge variant="outline" className="text-xs text-orange-600 border-orange-300">modified</Badge>}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="outline" onClick={() => editorDeleteFile(activeFile)} disabled={!activeFile || activeFile === fileEditorOffer?.index_file} aria-label={activeFile === fileEditorOffer?.index_file ? t('Set another entry page first') : t('Delete file')} title={activeFile === fileEditorOffer?.index_file ? t('Set another entry page first') : t('Delete file')}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                      <Button size="sm" onClick={saveFile} disabled={fileSaving || !fileDirty}>
                        <Save className="w-4 h-4 mr-1" />
                        {fileSaving ? t('Saving...') : t('Save')}
                      </Button>
                    </div>
                  </div>
                  <textarea
                    className="flex-1 w-full font-mono text-sm p-3 border rounded-lg resize-none bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                    value={fileContent}
                    onChange={(e) => { setFileContent(e.target.value); setFileDirty(true); }}
                    spellCheck={false}
                  />
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-muted-foreground">
                  <div className="text-center">
                    <FileCode className="w-12 h-12 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">{t('Select a file from the sidebar to edit')}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
